import {round, sumBy, last, sortBy, get, moment, transform, createSelector, groupBy, keyBy, castArray, toNumber, Moment} from 'helpers';
import {today, date} from 'store/common';
import {goal} from './goal';
import {goals} from './goals';
import {RootState} from 'store/root-state.type';

export const progressIndex = createSelector(
  [(state: RootState) => state.goals.progress, goals],
  (progress, goals) => ({
    ...groupBy(progress, 'goal_id'),
    ...keyBy(progress, record => {
      const goal = goals[record.goal_id];
      if (goal && (!goal.starting_date || record.timestamp >= goal.starting_date)) {
        return record.goal_id + '_' + record.timestamp;
      }
      return '__';
    })
  })
);

export const progressDetails = createSelector(
  [progressIndex, goal],
  (progressIndex, goal) => {
    const {id = '', starting_date, starting_value} = goal ?? {};
    const data = castArray(progressIndex[id] || []);
    if (starting_date && starting_value) {
      data.push({
        timestamp: starting_date - 24 * 60 * 60,
        value: toNumber(starting_value),
        goal_id: id,
        id: '0'
      });
    }
    return sortBy(data, 'timestamp');
  },
  {memoizeOptions: {maxSize: 100}},
);

export const progressSummary = createSelector(
  [progressDetails, goal],
  (progressDetails, goal) => {
    const {progressType, target_value = 0, unit} = goal ?? {};
    return {
      value: progressType == 'cumulative' ? get(last(progressDetails), 'value') : round(sumBy(progressDetails, 'value'), 3),
      total: round(target_value, 3),
      unit
    }
  }
);

export const progressChart = createSelector(
  [goal, progressDetails, date, today],
  (goal, progressDetails, date, today): {moment: Moment, value: number}[] => {
    const begin = date?.unix() ?? 0;
    if (goal?.progressType == 'cumulative') {
      return transform(progressDetails, (result, {timestamp, value}) => {
        if (begin <= timestamp) {
          result.push({
            moment: moment.unix(timestamp),
            value
          });
        }
      });
    }
    let prev: {moment: Moment, value: number} | undefined;
    const result = transform(progressDetails, (result: {moment: Moment, value: number}[], {timestamp, value}) => {
      const current = {
        moment: moment.unix(timestamp),
        value: value + (prev?.value ?? 0),
      }
      if (begin <= timestamp) {
        if (prev) {
          const diff = current.moment.diff(prev.moment, 'days');
          const max = current.moment.diff(date, 'days');
          for (let i = 1; i < Math.min(diff, max, 100); i++) {
            result.push({
              moment: current.moment.clone().subtract(i, 'days'),
              value: prev.value,
            });
          }
        }
        result.push(current);
      }
      prev = current;
    });
    if (prev && !prev.moment.isSame(today)) {
      const diff = today.diff(prev.moment, 'day');
      for (let i = 1; i < Math.min(diff, 100); i++) {
        result.push({
          moment: prev.moment.clone().add(i, 'days'),
          value: prev.value,
        });
      }
    }
    return result;
  },
  {memoizeOptions: {maxSize: 50}},
);
